import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject, of } from 'rxjs';
import { first, switchMap, take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { CmsPage } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';

import { PicalunaSiteService } from '../../services/picaluna-site.service';
import { contentSlug } from '../../content-slug';
import { ContentPageComponent } from './content-page.component';
import { globalBlockTokens } from './global-blocks';

/**
 * CMS pages list (PAGE-CMS.md phase 2). Clone of the blog list: persists via
 * accountService.patchContentItem('pages', …); per-row ☁ publishes just that
 * page's scope token to the staging preview.
 *
 * Locked pages (the pre-CMS sitemap — the SEO baseline) can't be removed and
 * their path can't be edited; unpublishing one only falls the site back to
 * its YAML version, it does NOT take the route down.
 */
@Component({
  selector: 'app-content-page-list',
  templateUrl: './content-page-list.component.html',
})
export class ContentPageListComponent implements OnInit, OnDestroy {
  /** True until ensureFullAccount() resolves — see the blog list. */
  accountLoading = true;
  /** The account fetch failed — the table must not claim there are no pages. */
  loadFailed = false;

  private unsubscribe$ = new Subject<void>();
  account$: Observable<Account>;

  publishingSlug: string | null = null;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    private picalunaSiteService: PicalunaSiteService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: (err) => {
        this.accountLoading = false;
        // Without this the modal renders "No pages yet" — indistinguishable
        // from a healthy empty account, and an invitation to create duplicates
        // of pages that are actually there.
        this.loadFailed = true;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  pagesAsList(content: any): CmsPage[] {
    const data = content?.pages;
    if (!data) return [];
    const list = Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean);
    return _.sortBy(list as CmsPage[], (p) => p?.path ?? '');
  }

  /**
   * The Pages table without the region-hub pages: a designed region page is
   * managed from Website → Regions (its Design-page button), not here —
   * listing the same object in two places was exactly the "why have both?"
   * confusion (Ivan, 2026-08-21). One door per thing.
   */
  visiblePages(content: any): CmsPage[] {
    const hidden = this.locationDesignedPaths(content);
    return this.pagesAsList(content).filter(
      (p) => !hidden.has((String(p.path || '').trim().replace(/\/+$/, '') || '/'))
    );
  }

  regionPageCount(content: any): number {
    const hidden = this.locationDesignedPaths(content);
    return this.pagesAsList(content).filter(
      (p) => hidden.has((String(p.path || '').trim().replace(/\/+$/, '') || '/'))
    ).length;
  }

  /** Region hubs AND suburb funeral-services addresses — pages there are
   *  designed from the Region/Suburb editors, not this list. */
  private locationDesignedPaths(content: any): Set<string> {
    const rawRegions: any = content?.regions;
    const regions = (Array.isArray(rawRegions) ? rawRegions : Object.values(rawRegions || {})).filter(
      (r: any) => r && typeof r === 'object' && typeof r.slug === 'string' && r.slug
    );
    const out = new Set<string>(
      regions.map((r: any) => `/${(r.state || 'nsw').toLowerCase()}/${r.slug}`)
    );
    const regionById = new Map(regions.map((r: any) => [r?._meta?.id, r]));
    const rawSuburbs: any = content?.suburbs;
    for (const sb of (Array.isArray(rawSuburbs) ? rawSuburbs : Object.values(rawSuburbs || {}))) {
      const r: any = sb && regionById.get((sb as any).regionId);
      if (!r || !(sb as any).slug) continue;
      out.add(`/${(r.state || 'nsw').toLowerCase()}/${r.slug}/funeral-services/${(sb as any).slug}`);
    }
    return out;
  }

  sectionCount(page: CmsPage): number {
    const s = page?.sections;
    if (!s || typeof s !== 'object') return 0;
    return Object.values(s).filter((x) => x && typeof x === 'object').length;
  }

  /**
   * The scope-token id for a page — MUST mirror pageSlugFromPath in
   * picaluna-v3's sync-conductor.ts ("/" → home, "/about/story" → about-story)
   * or the per-page publish targets the wrong (or no) item.
   */
  pageSlug(path: string): string {
    const trimmed = (path || '').replace(/^\/+|\/+$/g, '');
    return trimmed === '' ? 'home' : contentSlug(trimmed.replace(/\//g, '-'));
  }

  onPublishPage(page: CmsPage): void {
    const slug = this.pageSlug(page?.path || '');
    if (!slug || this.publishingSlug) {
      return;
    }

    const isLive = page.published === true;
    const title = page.title || page.path;

    const modal = this.modalService.generic({
      title: isLive ? 'Publish this page?' : 'Publish the removal of this page?',
      copy: isLive
        ? [
            `This publishes "${title}" (${page.path}) to the website preview.`,
            'Only this page is published — everything else on the site stays exactly as it is now.',
          ]
        : [
            `"${title}" is unpublished in Conductor, so this makes the preview fall back to the page's built-in version (or removes it, if it only ever existed here).`,
            'Turn the Published switch on first if you meant to put your edits live.',
          ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        {
          label: isLive ? 'Publish this page' : 'Publish removal',
          key: 'publish',
          class: isLive ? 'p-button-success' : 'p-button-danger',
        },
      ],
    });

    modal?.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key: string) => {
        if (key !== 'publish') {
          return;
        }

        this.publishingSlug = slug;

        this.picalunaSiteService
          .publish(this.publishScope(page))
          // take(1), not takeUntil: the publish DISPATCH must outlive the
          // dialog — takeUntil aborted it mid-flight and skipped the
          // publishedSlug breadcrumb, so the NEXT rename lost its redirect.
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.publishingSlug = null;
              this.recordPublishedSlug(page._meta.id, slug);
              this.modalService.generic({
                title: 'Publishing started',
                copy: [
                  `"${title}" is being published. It takes a few minutes.`,
                  'Track progress on the Website page, then publish to production from there when you are happy with the preview.',
                ],
                buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
              });
            },
            error: (err) => {
              this.publishingSlug = null;
              this.modalService.error(err?.error?.error || err);
            },
          });
      },
    });
  }

  onAddPage(): void {
    const seed: CmsPage = {
      _meta: { id: `id-page-${this.shortId()}`, index: 0 },
      path: '',
      title: '',
      published: false,
      sections: {},
    };
    this.openEditor(seed, true);
  }

  onEditPage(page: CmsPage): void {
    this.openEditor(_.cloneDeep(page), false);
  }

  onDeletePage(page: CmsPage): void {
    if (page.locked) {
      return;
    }
    const modal = this.modalService.generic({
      title: 'Remove page',
      copy: [
        `Remove "${page.title || page.path}"? To take it off the live site too, publish it after removing (the publish makes the site match Conductor).`,
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() !== 'confirm') return;
        this.accountService
          .removeContentItem('pages', page._meta.id)
          .pipe(take(1))
          // Silence here meant the row vanished from the table while the
          // server had rejected the delete — the next reload brought it back.
          .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
      },
    });
  }

  private openEditor(page: CmsPage, isNew: boolean): void {
    let account: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({ next: (a) => (account = a) });
    const otherPagePaths = this.pagesAsList(account?.data?.content)
      .filter((p) => p?._meta?.id !== page._meta.id)
      .map((p) => p.path)
      .filter(Boolean);

    // Regions (slug + state) for the §9.1 bespoke-region-path validation.
    const rawRegions: any = account?.data?.content?.regions;
    const regions = (Array.isArray(rawRegions) ? rawRegions : Object.values(rawRegions || {}))
      .filter((r: any) => r && typeof r === 'object' && typeof r.slug === 'string' && r.published !== false)
      .map((r: any) => ({ slug: r.slug, state: r.state }));
    // Suburb designed-page addresses (sans leading slash) — the allowed
    // 4-segment forms inside the locations area.
    const suburbPaths = [...this.locationDesignedPaths(account?.data?.content)]
      .filter((path) => path.split('/').filter(Boolean).length === 4)
      .map((path) => path.replace(/^\//, ''));

    const ref = this.dialogService.open(ContentPageComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: isNew ? 'New page' : `Edit ${page.title || page.path}`,
      width: '90vw',
      baseZIndex: 11000,
      data: {
        page,
        isNew,
        otherPagePaths,
        regions,
        suburbPaths,
        // The editor performs this save ITSELF and closes only on success —
        // the Save button spins while the request is in flight, so a person
        // can no longer save, see the dialog vanish, and hit Publish before
        // the write has actually landed.
        save: (p: CmsPage) => this.accountService.patchContentItem('pages', p).pipe(take(1)),
      },
    });
    ref.onClose.subscribe({
      next: (result: { saved: boolean; page: CmsPage; persisted?: boolean } | undefined) => {
        if (!result?.saved || result.persisted) return;
        // Legacy fallback only (an editor without the save callback) — see
        // the note in the menu list: a write must outlive its dialog.
        this.accountService
          .patchContentItem('pages', result.page)
          .pipe(take(1))
          .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
      },
    });
  }

  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  /**
   * `sections` keyed by each section's `_meta.id`, whatever shape it arrived
   * in. The map key IS the section's identity on the server and in the site's
   * asset filenames; an array round-trip silently renumbers them.
   */
  private sectionsAsMap(page: CmsPage): Record<string, any> {
    const raw: any = page?.sections;
    if (!raw) return {};
    const list: any[] = Array.isArray(raw) ? raw.filter(Boolean) : Object.values(raw).filter(Boolean);
    const map: Record<string, any> = {};
    list.forEach((s, i) => {
      let id = s?._meta?.id || `id-s${(i + 1) * 10}`;
      // Two sections sharing an id would collapse into one, losing a section
      // on a write that was only meant to record a published slug.
      while (map[id]) id = `${id}-${i}`;
      map[id] = { ...s, _meta: { id, index: typeof s?._meta?.index === 'number' ? s._meta.index : i * 10 } };
    });
    return map;
  }

  /** Old-slug + new-slug tokens on a rename — see the blog list's publishScope. */
  private publishScope(page: CmsPage): string {
    const slug = this.pageSlug(page.path || '');
    const prev = (page.publishedSlug || '').trim();
    const pages = prev && prev !== slug ? `pages:${prev} pages:${slug}` : `pages:${slug}`;
    // The global blocks the page places ride along (#217), so a newly placed one shows on the preview. The promote
    // dialog still lists each as its own row.
    return [pages, ...globalBlockTokens(page.sections)].join(' ');
  }

  /** Breadcrumb for later renames — see the blog list's recordPublishedSlug. */
  private recordPublishedSlug(metaId: string, slug: string): void {
    this.accountService
      .get(true)
      .pipe(first())
      .subscribe({
        next: () => {
          let account: Account | undefined;
          this.accountService.account$.pipe(first()).subscribe({ next: (a) => (account = a) });
          const raw = account?.data?.content?.pages;
          const rows: CmsPage[] = !raw
            ? []
            : Array.isArray(raw)
              ? raw
              : (Object.values(raw) as CmsPage[]);
          const fresh = rows.find((r) => r?._meta?.id === metaId);
          if (!fresh || fresh.publishedSlug === slug) return;
          fresh.publishedSlug = slug;
          // The account stream has already run objectsToArrays, so `sections`
          // arrives here as an ARRAY. Sending it back that way rewrote every
          // section under a numeric key, losing the id the section's uploaded
          // image is named after. Rebuild the id-keyed map before writing.
          this.accountService
            .patchContentItem('pages', { ...fresh, sections: this.sectionsAsMap(fresh) })
            .pipe(first())
            .subscribe({ error: () => {} });
        },
        error: () => {},
      });
  }
}
